import axios from 'axios';
import type {
  Paciente,
  Profesional,
  Cita,
  Tratamiento,
  Especialidad,
  EstadisticaPacientesActivos,
  EstadisticaCitasPorMes,
  EstadisticaCancelacionesPorMes,
  EstadisticaTasaCancelacion,
  EstadisticaTratamientosSolicitados,
  EstadisticaIngresosTratamiento,
  EstadisticaRankingProfesionales,
  EstadisticaDistribucionGeneroEdad,
  EstadisticaEspecialidadesDemandadas,
  EstadisticaHorasPico,
  ApiResponse,
  LoginResponse,
  CitaFilters,
  PacientesResponse,
  PacienteResponse,
  ProfesionalesResponse,
  ProfesionalResponse,
  CitasResponse,
  CitaResponse,
  TratamientosResponse,
  TratamientoResponse,
  EspecialidadesResponse,
  EspecialidadResponse,
  EstadisticaPacientesActivosResponse,
  EstadisticaCitasPorMesResponse,
  EstadisticaCancelacionesPorMesResponse,
  EstadisticaTasaCancelacionResponse,
  EstadisticaTratamientosSolicitadosResponse,
  EstadisticaIngresosTratamientoResponse,
  EstadisticaRankingProfesionalesResponse,
  EstadisticaDistribucionGeneroEdadResponse,
  EstadisticaEspecialidadesDemandadasResponse,
  EstadisticaHorasPicoResponse
} from '../types/api';

// Configuración base de Axios
const API_BASE_URL = 'https://node-api-clinica-sg-production.up.railway.app';
// const API_BASE_URL = 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API de Pacientes
export const pacientesApi = {
  getAll: async (filters?: any): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<any>(`/api/pacientes?${params.toString()}`);
    return {
      data: (response.data as any)?.pacientes ?? (response.data as any)?.data?.pacientes ?? [],
      total: (response.data as any)?.total ?? (response.data as any)?.data?.total ?? 0,
      page: (response.data as any)?.page ?? 1,
      limit: (response.data as any)?.limit ?? null
    };
  },
  getById: async (id: number): Promise<Paciente> => {
    const response = await api.get<any>(`/api/pacientes/${id}`);
    return (response.data as any)?.paciente ?? (response.data as any)?.data?.paciente;
  },
  create: async (data: Omit<Paciente, 'id' | 'created_at' | 'updated_at'>): Promise<Paciente> => {
    const response = await api.post<any>('/api/pacientes', data);
    return (response.data as any)?.paciente ?? (response.data as any)?.data?.paciente;
  },
  update: async (id: number, data: Partial<Paciente>): Promise<Paciente> => {
    const response = await api.patch<any>(`/api/pacientes/${id}`, data);
    return (response.data as any)?.paciente ?? (response.data as any)?.data?.paciente;
  },
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<any>(`/api/pacientes/${id}`);
    return { message: (response.data as any)?.message || 'Paciente eliminado correctamente' };
  },
};

// API de Profesionales
export const profesionalesApi = {
  getAll: async (filters?: any): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<any>(`/api/profesionales?${params.toString()}`);
    return {
      data: (response.data as any)?.profesionales ?? (response.data as any)?.data?.profesionales ?? [],
      total: (response.data as any)?.total ?? (response.data as any)?.data?.total ?? 0,
      page: (response.data as any)?.page ?? 1,
      limit: (response.data as any)?.limit ?? null
    };
  },
  getById: async (id: number): Promise<Profesional> => {
    const response = await api.get<any>(`/api/profesionales/${id}`);
    return (response.data as any)?.profesional ?? (response.data as any)?.data?.profesional;
  },
  create: async (data: Omit<Profesional, 'id' | 'created_at' | 'updated_at'>): Promise<Profesional> => {
    const response = await api.post<any>('/api/profesionales', data);
    return (response.data as any)?.profesional ?? (response.data as any)?.data?.profesional;
  },
  update: async (id: number, data: Partial<Profesional>): Promise<Profesional> => {
    const response = await api.patch<any>(`/api/profesionales/${id}`, data);
    return (response.data as any)?.profesional ?? (response.data as any)?.data?.profesional;
  },
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<any>(`/api/profesionales/${id}`);
    return { message: (response.data as any)?.message || 'Profesional desactivado correctamente' };
  },
};

// API de Citas
export const citasApi = {
  getAll: async (filters?: CitaFilters): Promise<Cita[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<any>(`/api/citas?${params.toString()}`);
    console.log('API Response raw:', response);
    console.log('API Response data:', response.data);
    
    // Manejar la estructura: { status, results, data: { citas: [...] } }
    let citas = [];
    if (response.data) {
      if (Array.isArray(response.data)) {
        // Si response.data es directamente un array
        citas = response.data;
      } else if (response.data.data && response.data.data.citas) {
        // Si tiene la estructura { status, results, data: { citas } }
        citas = response.data.data.citas;
      } else if (response.data.citas) {
        // Si tiene la estructura { citas: [...] }
        citas = response.data.citas;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // Si data es directamente un array
        citas = response.data.data;
      }
    }
    
    console.log('Extracted citas:', citas);
    return Array.isArray(citas) ? citas : [];
  },
  getById: async (id: number): Promise<Cita> => {
    const response = await api.get<any>(`/api/citas/${id}`);
    return (response.data as any)?.cita ?? (response.data as any)?.data?.cita;
  },
  create: async (data: Omit<Cita, 'id' | 'created_at' | 'updated_at'>): Promise<Cita> => {
    const response = await api.post<any>('/api/citas', data);
    return (response.data as any)?.cita ?? (response.data as any)?.data?.cita;
  },
  update: async (id: number, data: Partial<Cita>): Promise<Cita> => {
    const response = await api.patch<any>(`/api/citas/${id}`, data);
    return (response.data as any)?.cita ?? (response.data as any)?.data?.cita;
  },
  cancelar: async (id: number, motivo?: string): Promise<{ message: string; cita: Cita }> => {
    const response = await api.patch<any>(`/api/citas/${id}/cancelar`, { motivo_cancelacion: motivo });
    const cita = (response.data as any)?.cita ?? (response.data as any)?.data?.cita;
    return { 
      message: (response.data as any)?.message || 'Cita cancelada correctamente',
      cita
    };
  },
  completar: async (id: number): Promise<{ message: string; cita: Cita }> => {
    const response = await api.patch<any>(`/api/citas/${id}/completar`);
    const cita = (response.data as any)?.cita ?? (response.data as any)?.data?.cita;
    return { 
      message: (response.data as any)?.message || 'Cita completada correctamente',
      cita
    };
  },
};

// API de Tratamientos
export const tratamientosApi = {
  getAll: async (filters?: any): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<any>(`/api/tratamientos?${params.toString()}`);
    return {
      data: (response.data as any)?.tratamientos ?? (response.data as any)?.data?.tratamientos ?? [],
      total: (response.data as any)?.total ?? (response.data as any)?.data?.total ?? 0,
      page: (response.data as any)?.page ?? 1,
      limit: (response.data as any)?.limit ?? null
    };
  },
  getById: async (id: number): Promise<Tratamiento> => {
    const response = await api.get<any>(`/api/tratamientos/${id}`);
    return (response.data as any)?.tratamiento ?? (response.data as any)?.data?.tratamiento;
  },
  create: async (data: Omit<Tratamiento, 'id' | 'created_at' | 'updated_at'>): Promise<Tratamiento> => {
    const response = await api.post<any>('/api/tratamientos', data);
    return (response.data as any)?.tratamiento ?? (response.data as any)?.data?.tratamiento;
  },
  update: async (id: number, data: Partial<Tratamiento>): Promise<Tratamiento> => {
    const response = await api.patch<any>(`/api/tratamientos/${id}`, data);
    return (response.data as any)?.tratamiento ?? (response.data as any)?.data?.tratamiento;
  },
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<any>(`/api/tratamientos/${id}`);
    return { message: (response.data as any)?.message || 'Tratamiento desactivado correctamente' };
  },
};

// API de Especialidades
export const especialidadesApi = {
  getAll: async (filters?: any): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const response = await api.get<any>(`/api/especialidades?${params.toString()}`);
    return {
      data: (response.data as any)?.especialidades ?? (response.data as any)?.data?.especialidades ?? [],
      total: (response.data as any)?.total ?? (response.data as any)?.data?.total ?? 0,
      page: (response.data as any)?.page ?? 1,
      limit: (response.data as any)?.limit ?? null
    };
  },
  getById: async (id: number): Promise<Especialidad> => {
    const response = await api.get<any>(`/api/especialidades/${id}`);
    return (response.data as any)?.especialidad ?? (response.data as any)?.data?.especialidad;
  },
  create: async (data: Omit<Especialidad, 'id' | 'created_at' | 'updated_at'>): Promise<Especialidad> => {
    const response = await api.post<any>('/api/especialidades', data);
    return (response.data as any)?.especialidad ?? (response.data as any)?.data?.especialidad;
  },
  update: async (id: number, data: Partial<Especialidad>): Promise<Especialidad> => {
    const response = await api.patch<any>(`/api/especialidades/${id}`, data);
    return (response.data as any)?.especialidad ?? (response.data as any)?.data?.especialidad;
  },
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<any>(`/api/especialidades/${id}`);
    return { message: (response.data as any)?.message || 'Especialidad eliminada correctamente' };
  },
};

// API de Estadísticas
export const estadisticasApi = {
  getPacientesActivos: async (): Promise<EstadisticaPacientesActivos> => {
    const response = await api.get<any>('/api/estadisticas/pacientes-activos');
    const total = (response.data as any)?.total_pacientes_activos ?? 
                 (response.data as any)?.data?.total_pacientes_activos ?? 
                 (response.data as any)?.total ?? 0;
    return { total };
  },
  getCitasAgrupadas: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
    }
    const response = await api.get<any>(`/api/estadisticas/citas-agrupadas?${params.toString()}`);
    const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data ?? [];
    return data;
  },
  getCancelacionesAgrupadas: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
    }
    const response = await api.get<any>(`/api/estadisticas/cancelaciones-agrupadas?${params.toString()}`);
    const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data ?? [];
    return data;
  },
  getTasaCancelacionAgrupada: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
    }
    const response = await api.get<any>(`/api/estadisticas/tasa-cancelacion-agrupada?${params.toString()}`);
    const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data ?? [];
    return data;
  },
  getTratamientosMasSolicitadosAgrupados: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string, top?: number }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
      if (filters.top) params.append('top', String(filters.top));
    }
    const response = await api.get<any>(`/api/estadisticas/tratamientos-mas-solicitados-agrupados?${params.toString()}`);
    return (response.data as any)?.data ?? {};
  },
  getIngresosPorTratamientoAgrupados: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
    }
    const response = await api.get<any>(`/api/estadisticas/ingresos-por-tratamiento-agrupados?${params.toString()}`);
    return (response.data as any)?.data ?? [];
  },
  getRankingProfesionalesAgrupado: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string, top?: number }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
      if (filters.top) params.append('top', String(filters.top));
    }
    const response = await api.get<any>(`/api/estadisticas/ranking-profesionales-agrupado?${params.toString()}`);
    return (response.data as any)?.data ?? {};
  },
  getEspecialidadesDemandadasAgrupadas: async (filters?: { fecha_inicio?: string, fecha_fin?: string, group_by?: string, top?: number }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
      if (filters.group_by) params.append('group_by', filters.group_by);
      if (filters.top) params.append('top', String(filters.top));
    }
    const response = await api.get<any>(`/api/estadisticas/especialidades-demandadas-agrupadas?${params.toString()}`);
    return (response.data as any)?.data ?? {};
  },
  getHorasPicoAgrupadas: async (filters?: { fecha_inicio?: string, fecha_fin?: string }): Promise<any[]> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
    }
    const response = await api.get<any>(`/api/estadisticas/horas-pico-agrupadas?${params.toString()}`);
    return (response.data as any)?.data ?? [];
  },
  getDistribucionGeneroEdad: async (): Promise<any[]> => {
    const response = await api.get<any>(`/api/estadisticas/distribucion-genero-edad`);
    return (response.data as any)?.data ?? [];
  },
};

// API de Autenticación (por implementar en el backend)
export const authApi = {
  login: async (email: string, password: string): Promise<{ token: string; user: any }> => {
    const response = await api.post<LoginResponse>('/api/auth/login', { email, password });
    return { 
      token: response.data.token, 
      user: response.data.data.usuario 
    };
  },
  register: async (userData: any): Promise<any> => {
    const response = await api.post<any>('/api/auth/register', userData);
    return (response.data as any)?.data ?? response.data;
  },
  logout: async (): Promise<{ message: string }> => {
    const response = await api.post<any>('/api/auth/logout');
    return { message: (response.data as any)?.message || 'Sesión cerrada correctamente' };
  },
  getProfile: async (): Promise<any> => {
    const response = await api.get<any>('/api/auth/profile');
    return (response.data as any)?.data ?? response.data;
  },
};

export default api; 